import { yupResolver } from '@hookform/resolvers/yup';
import Modal from 'components/desktop/shared/modal';
import ToastStack from 'components/desktop/shared/toast-stack';
import {
  IconBuilding,
  IconHouseDoor,
  IconLoading,
  IconLocationFill,
  IconVectorSquareClose,
} from 'components/icons';
import { useAuthenticationContext } from 'components/providers/authentication-provider';
import FormTextArea from 'components/shared/form-text-area';
import FormTextInput from 'components/shared/form-text-input';
import {
  RegisterAdviseForm,
  registerAdviseFormDefaultValues,
  registerAdviseFormSchema,
} from 'forms';
import {
  CREATE_REGISTER_ADVISE,
  CreateRegisterAdviseData,
  CreateRegisterAdviseVariables,
} from 'graphql/main/mutations';
import { FileWithFullUrls } from 'graphql/main/queries';
import { useFlexMutation, useToast } from 'hooks';
import { filter, isEmpty, isEqual, map, size, some, toString } from 'lodash';
import { DetailProjectPageContext } from 'pages/du-an/[chi-tiet-du-an]';
import { useContext, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

interface Props {
  onClose?: () => void;
}

const RequestAdviseModal = ({ onClose }: Props) => {
  const { currentUser } = useAuthenticationContext();
  const { project } = useContext(DetailProjectPageContext).pageData ?? {};
  const { id, project: projectDetail } = project ?? {};
  const { projectName, fullAddress, totalBlock, blocks, projectArea, apartmentCount, mediaUrls } =
    projectDetail ?? {};
  const { area } = projectArea ?? {};
  const { toasts, addToast, removeToast } = useToast();
  const form = useForm<RegisterAdviseForm>({
    mode: 'onChange',
    resolver: yupResolver(registerAdviseFormSchema),
    defaultValues: registerAdviseFormDefaultValues,
  });
  const [createRegisterAdvise, { loading: loadingCreateRegisterAdvise }] = useFlexMutation<
    CreateRegisterAdviseData,
    CreateRegisterAdviseVariables
  >('main', CREATE_REGISTER_ADVISE);
  const image = filter(mediaUrls, (media) => isEqual('image', media.type))?.[0];
  const stack = [
    {
      icon: <IconVectorSquareClose className='h-[16px] w-[16px] text-primary' />,
      content: area?.toLocaleString(),
      suffix: 'm\u00B2',
    },
    {
      icon: <IconBuilding className='h-[16px] w-[16px] text-primary' />,
      content: totalBlock
        ? totalBlock.toLocaleString()
        : blocks
        ? size(blocks).toLocaleString()
        : undefined,
      suffix: 'block',
    },
    {
      icon: <IconHouseDoor className='h-[16px] w-[16px] text-primary' />,
      content: apartmentCount?.toLocaleString(),
      suffix: 'căn',
    },
  ];

  const submit = async (data: RegisterAdviseForm) => {
    const { fullName, phone, email, note } = data;
    const { errors } = await createRegisterAdvise({
      variables: {
        input: {
          publishedProject: toString(id),
          fullName: toString(fullName),
          phone: toString(phone),
          email: isEmpty(email) ? undefined : email,
          note: isEmpty(note) ? undefined : note,
        },
      },
      onCompleted: (data) => {
        data?.createRegisterAdvise?.success
          ? addToast('success', data.createRegisterAdvise?.msg ?? 'Gửi yêu cầu tư vấn thành công')
          : addToast('error', data.createRegisterAdvise?.msg ?? 'Gửi yêu cầu tư vấn thất bại');
      },
      onError: (error) => addToast('error', error.message),
    });
    if (errors) addToast('error', errors[0].message ?? 'Gửi yêu cầu tư vấn thất bại');
  };

  useEffect(() => {
    if (currentUser) {
      {
        form.setValue('fullName', currentUser.fullName);
        form.setValue('phone', currentUser.phones?.[0]);
        form.setValue('email', currentUser.email);
      }
    }
  }, []);

  return (
    <Modal
      closable
      title='Yêu cầu tư vấn'
      className='fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 overflow-hidden'
      onClose={onClose}
    >
      <div className='mx-[-24px] mb-[-24px] mt-[-8px] flex'>
        <div className='flex w-[430px] flex-col space-y-[24px]'>
          <div className='relative h-[304px]'>
            <img
              src={image ? (image as FileWithFullUrls).originalUrl : '/images/error-image.svg'}
              alt={projectName}
              loading='lazy'
              className='h-full w-full object-cover'
            />
          </div>
          <div className='flex flex-col space-y-[24px] px-[24px] pb-[24px]'>
            {projectName && (
              <span className='line-clamp-2 text-[16px] font-[700] leading-[20px]'>
                {projectName}
              </span>
            )}
            {fullAddress && (
              <div className='flex items-center space-x-[6px]'>
                <IconLocationFill className='mt-[1px] h-[24px] min-w-[24px] text-text2' />
                <span className='line-clamp-2'>{fullAddress}</span>
              </div>
            )}
            {some(stack, (item) => item.content) && (
              <div className='m-[-8px] flex max-h-[78px] flex-wrap items-center overflow-hidden'>
                {map(
                  stack,
                  (stackItem, stackItemIndex) =>
                    stackItem.content && (
                      <div
                        key={stackItemIndex}
                        className='flex items-center space-x-[8px] whitespace-nowrap p-[8px]'
                      >
                        {stackItem.icon}
                        <span>
                          {stackItem.content} {stackItem.suffix}
                        </span>
                      </div>
                    ),
                )}
              </div>
            )}
          </div>
        </div>
        <FormProvider {...form}>
          <form
            className='flex w-[430px] flex-col justify-between space-y-[28px] bg-[#fafafa] p-[24px]'
            onSubmit={form.handleSubmit(submit)}
          >
            <div className='flex flex-col space-y-[28px]'>
              <FormTextInput
                maxLength={118}
                label='Họ và tên'
                required
                name='fullName'
                autoComplete='off'
                error={form.formState.errors.fullName}
              />
              <FormTextInput
                label='Số điện thoại'
                required
                name='phone'
                maxLength={10}
                autoComplete='off'
                error={form.formState.errors.phone}
              />
              <FormTextInput
                label='Email'
                required
                maxLength={68}
                name='email'
                autoComplete='off'
                error={form.formState.errors.email}
              />
              <FormTextArea
                label='Ghi chú'
                name='note'
                autoComplete='off'
                rows={3}
                maxLength={888}
                error={form.formState.errors.note}
              />
            </div>
            <button
              disabled={loadingCreateRegisterAdvise}
              className='flex h-[40px] w-full items-center justify-center space-x-[6px] rounded-[8px] bg-primary text-paper transition duration-[200ms] ease-in-out hover:bg-primary-light disabled:opacity-50 disabled:hover:bg-primary'
              onClick={form.handleSubmit(submit)}
            >
              {loadingCreateRegisterAdvise && <IconLoading className='animate-spin' />}
              <span className='font-[600]'>Đăng ký ngay</span>
            </button>
          </form>
        </FormProvider>
        <ToastStack toasts={toasts} removeToast={removeToast} />
      </div>
    </Modal>
  );
};

export default RequestAdviseModal;
